import { defineStore } from 'pinia'
import { Network } from '@shared/types'
import { COSMOS_NETWORKS } from '@/app/data/network'
import { transactionCompleteStates } from '@/app/data/transaction'
import {
  // mapCosmosTransaction,
  // mapPeggyDepositToPeggo,
  mapWormholeMigrationTransaction
  // mapPeggyDepositToInjectiveTransaction,
  // mapPeggyWithdrawalToInjectiveTransaction
} from '@/store/pendingTransaction/utils'
import type { UiPendingTransaction, UiPendingTransactionWithKey } from '@/types'

type PendingTransactionStoreState = {
  activeTransactionKey?: string
  transactions: UiPendingTransactionWithKey[]
}

const initialStateFactory = (): PendingTransactionStoreState => ({
  transactions: [],
  activeTransactionKey: undefined
})

export const usePendingTransactionStore = defineStore('pendingTransaction', {
  state: (): PendingTransactionStoreState => initialStateFactory(),
  getters: {
    incompleteTransactions: (state) =>
      state.transactions.filter(
        (transaction) => !transactionCompleteStates.includes(transaction.state)
      )
  },
  actions: {
    append(transaction: UiPendingTransaction) {
      const pendingTransactionStore = usePendingTransactionStore()

      const id = `${transaction.network}-${transaction.isDeposit}-${Date.now()}`

      pendingTransactionStore.$patch({
        activeTransactionKey: id,
        transactions: [
          { ...transaction, id },
          ...pendingTransactionStore.transactions
        ]
      })
    },

    replace(transactions: UiPendingTransactionWithKey[]) {
      const pendingTransactionStore = usePendingTransactionStore()

      pendingTransactionStore.$patch({
        transactions
      })
    },

    clearActiveTransaction() {
      const pendingTransactionStore = usePendingTransactionStore()

      pendingTransactionStore.$patch({ activeTransactionKey: undefined })
    },

    async updateCosmosDepositTransactionStatus() {
      const transactionStore = useTransactionStore()
      const pendingTransactionStore = usePendingTransactionStore()

      const pendingCosmosDepositTransaction =
        pendingTransactionStore.incompleteTransactions.filter(
          ({ network, isDeposit }) =>
            COSMOS_NETWORKS.includes(network) && isDeposit
        )

      if (pendingCosmosDepositTransaction.length === 0) {
        return
      }

      await transactionStore.fetchCosmosTransactions()
    },

    async updatePeggyDepositTransactionStatus() {
      const transactionStore = useTransactionStore()
      const pendingTransactionStore = usePendingTransactionStore()

      const pendingEthDepositTransaction =
        pendingTransactionStore.incompleteTransactions.filter(
          ({ network, isDeposit }) => network === Network.Ethereum && isDeposit
        )

      if (pendingEthDepositTransaction.length === 0) {
        return
      }

      await transactionStore.fetchPeggyDepositTransactions()
    },

    async updatePeggyWithdrawalTransactionStatus() {
      const transactionStore = useTransactionStore()
      const pendingTransactionStore = usePendingTransactionStore()

      const pendingEthDWithdrawalTransaction =
        pendingTransactionStore.incompleteTransactions.filter(
          ({ network, isDeposit }) => network === Network.Ethereum && !isDeposit
        )

      if (pendingEthDWithdrawalTransaction.length === 0) {
        return
      }

      await transactionStore.fetchPeggyWithdrawalTransactions()
    },

    async updateWormholeMigrationTransactionStatus() {
      const transactionStore = useTransactionStore()
      const pendingTransactionStore = usePendingTransactionStore()

      const pendingWormholeMigrationTransaction =
        pendingTransactionStore.incompleteTransactions.filter(
          ({ network, sender, receiver }) =>
            network === Network.Injective && sender === receiver
        )

      if (pendingWormholeMigrationTransaction.length === 0) {
        return
      }

      await transactionStore.fetchLatestTransactions()

      pendingTransactionStore.$patch({
        transactions: pendingTransactionStore.transactions.map(
          (pendingTransaction) =>
            mapWormholeMigrationTransaction({
              pendingTransaction,
              transactions: transactionStore.latestTransactions
            })
        )
      })
    },

    reset() {
      const pendingTransactionStore = usePendingTransactionStore()

      const initialState = initialStateFactory()

      pendingTransactionStore.$patch({
        ...initialState
      })
    }
  }
})
